import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BarChart3, Clock3, FileText, Loader2, PackageCheck, Plus, RefreshCw, TrendingUp } from 'lucide-react';
import api from '../api';

const S = `
  .sd-wrap { max-width: 1200px; margin: 0 auto; font-family: 'Inter', sans-serif; color: #111827; }
  .sd-head { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
  .sd-title { margin:0; font-size:22px; font-weight:800; color:#111827; }
  .sd-sub { margin-top:3px; font-size:13px; color:#1a1a1a; }
  .sd-btn { border:1px solid #d1d5db; border-radius:9px; background:#fff; color:#111827; padding:8px 12px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }
  .sd-btn:hover { background:#f9fafb; }
  .sd-btn-primary { background:#111827; color:#fff; border-color:#111827; }

  .sd-quick { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:18px; }

  .sd-kpis { display:grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap:12px; margin-bottom:18px; }
  .sd-kpi { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 2px 10px rgba(0,0,0,.05); padding:14px; }
  .sd-kpi-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
  .sd-kpi-label { font-size:11px; text-transform:uppercase; letter-spacing:.4px; color:#1a1a1a; font-weight:700; }
  .sd-kpi-value { font-size:24px; font-weight:900; color:#111827; }

  .sd-panel { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 2px 10px rgba(0,0,0,.05); padding:14px; margin-bottom:16px; }
  .sd-panel-title { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:800; color:#111827; margin-bottom:12px; }

  .sd-funnel { display:flex; align-items:stretch; gap:8px; overflow-x:auto; padding-bottom:2px; }
  .sd-stage { min-width:180px; background:#f9fafb; border:1px solid #e5e7eb; border-radius:12px; padding:12px; }
  .sd-stage .name { font-size:12px; color:#1a1a1a; font-weight:700; text-transform:uppercase; letter-spacing:.3px; }
  .sd-stage .count { font-size:22px; font-weight:900; color:#111827; margin:4px 0; }
  .sd-stage .value { font-size:12px; color:#1a1a1a; }
  .sd-arrow { display:flex; align-items:center; color:#9ca3af; }

  .sd-table-wrap { overflow:auto; }
  .sd-table { width:100%; border-collapse:collapse; min-width:620px; }
  .sd-table th { text-align:left; padding:10px 12px; font-size:11px; letter-spacing:.4px; color:#1a1a1a; border-bottom:2px solid #f3f4f6; text-transform:uppercase; }
  .sd-table td { padding:12px; border-bottom:1px solid #f9fafb; font-size:13px; color:#000; }

  .sd-skeleton { display:grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap:12px; margin-bottom:14px; }
  .sd-sk { height:88px; border-radius:12px; background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%); background-size:200% 100%; animation: shimmer 1.5s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  @media(max-width:1100px){ .sd-kpis{ grid-template-columns:repeat(3,minmax(0,1fr)); } .sd-skeleton{ grid-template-columns:repeat(3,minmax(0,1fr)); } }
  @media(max-width:700px){ .sd-kpis{ grid-template-columns:repeat(2,minmax(0,1fr)); } .sd-skeleton{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
  @media(max-width:500px){ .sd-kpis{ grid-template-columns:1fr; } .sd-skeleton{ grid-template-columns:1fr; } }
`;

const fmtRs = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function SalesDashboard({ onNavigate, onNewQuotation, onNewOrder }) {
  const [dashboard, setDashboard] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [dRes, tRes] = await Promise.all([
        api.get('/sales/dashboard'),
        api.get('/sales/top-clients', { params: { days: 30 } }),
      ]);
      setDashboard(dRes?.data || {});
      setTopClients(tRes?.data?.data || dRes?.data?.top_customers?.map((c) => ({
        client_name: c.name,
        order_count: null,
        total_value: c.value,
        last_order_date: null,
      })) || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load sales dashboard.');
      setDashboard(null);
      setTopClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const kpis = dashboard?.kpis || {};
  const stages = useMemo(() => {
    const p = dashboard?.pipeline || [];
    const desired = ['QUOTATION', 'APPROVED', 'IN_PRODUCTION', 'COMPLETED', 'DELIVERED'];
    const map = new Map(p.map((x) => [x.status, x]));
    return desired.map((s) => map.get(s) || { status: s, count: 0, value: 0 });
  }, [dashboard]);

  return (
    <>
      <style>{S}</style>
      <div className="sd-wrap">
        <div className="sd-head">
          <div>
            <h1 className="sd-title">Sales Dashboard</h1>
            <div className="sd-sub">Real-time sales KPIs, pipeline, and top clients.</div>
          </div>
          <button className="sd-btn" onClick={load}><RefreshCw size={14} style={{ marginRight: 6 }} />Refresh</button>
        </div>

        <div className="sd-quick">
          <button className="sd-btn sd-btn-primary" onClick={onNewQuotation}><Plus size={14} style={{ marginRight: 6 }} />New Quotation</button>
          <button className="sd-btn" onClick={onNewOrder}><Plus size={14} style={{ marginRight: 6 }} />New Order</button>
          <button className="sd-btn" onClick={() => onNavigate?.('quotations')}>Quotations</button>
          <button className="sd-btn" onClick={() => onNavigate?.('orders')}>Orders</button>
        </div>

        {loading ? (
          <>
            <div className="sd-skeleton">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="sd-sk" />)}</div>
            <div className="sd-panel" style={{ height: 110 }} />
            <div className="sd-panel" style={{ height: 180 }} />
          </>
        ) : error ? (
          <div className="sd-panel" style={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={16} /> {error}
          </div>
        ) : (
          <>
            <div className="sd-kpis">
              <div className="sd-kpi">
                <div className="sd-kpi-top"><span className="sd-kpi-label">Active Orders</span><Clock3 size={16} color="#2563eb" /></div>
                <div className="sd-kpi-value">{kpis.active_orders ?? 0}</div>
              </div>
              <div className="sd-kpi">
                <div className="sd-kpi-top"><span className="sd-kpi-label">Completed This Month</span><PackageCheck size={16} color="#16a34a" /></div>
                <div className="sd-kpi-value">{kpis.completed_this_month ?? 0}</div>
              </div>
              <div className="sd-kpi">
                <div className="sd-kpi-top"><span className="sd-kpi-label">Revenue This Month</span><TrendingUp size={16} color="#059669" /></div>
                <div className="sd-kpi-value" style={{ fontSize: 20 }}>{fmtRs(kpis.revenue_this_month)}</div>
              </div>
              <div className="sd-kpi">
                <div className="sd-kpi-top"><span className="sd-kpi-label">Pending Quotations</span><FileText size={16} color="#d97706" /></div>
                <div className="sd-kpi-value">{kpis.pending_quotations ?? 0}</div>
              </div>
              <div className="sd-kpi">
                <div className="sd-kpi-top"><span className="sd-kpi-label">Orders In Production</span><BarChart3 size={16} color="#7c3aed" /></div>
                <div className="sd-kpi-value">{kpis.in_production ?? 0}</div>
              </div>
            </div>

            <div className="sd-panel">
              <div className="sd-panel-title"><BarChart3 size={16} color="#4f46e5" />Pipeline Funnel</div>
              <div className="sd-funnel">
                {stages.map((st, idx) => (
                  <React.Fragment key={st.status}>
                    <div className="sd-stage">
                      <div className="name">{st.status.replace('_', ' ')}</div>
                      <div className="count">{st.count ?? 0}</div>
                      <div className="value">{fmtRs(st.value)}</div>
                    </div>
                    {idx < stages.length - 1 ? <div className="sd-arrow"><ArrowRight size={16} /></div> : null}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="sd-panel">
              <div className="sd-panel-title"><TrendingUp size={16} color="#4f46e5" />Top Clients (Last 30 Days)</div>
              <div className="sd-table-wrap">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Orders</th>
                      <th>Value</th>
                      <th>Last Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topClients.length === 0 ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#1a1a1a' }}>No client data</td></tr>
                    ) : topClients.map((row, idx) => (
                      <tr key={`${row.client_name}-${idx}`}>
                        <td style={{ fontWeight: 700, color: '#111827' }}>{row.client_name || '-'}</td>
                        <td>{row.order_count ?? '-'}</td>
                        <td style={{ fontWeight: 700 }}>{fmtRs(row.total_value)}</td>
                        <td>{fmtDate(row.last_order_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
